/**
 * /gift/:slug — Regalos, productos con reel, experiencia completa
 *
 * Si el producto tiene video_url o imágenes (reel), muestra ReelsToBuy.
 * Si no, muestra Card tradicional (features, tags, múltiples CTAs).
 *
 * Ejemplos: /gift/video-personalizado, /gift/pack-fotos-premium, /gift/regalo-especial
 *
 * No confundir con /p/:slug: productos individuales del dashboard, página minimalista.
 */

import { useEffect, useState } from 'react';
import { getCanonicalLocation } from "@/lib/canonicalLocation";
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, ShoppingCart } from 'lucide-react';
import ReelsToBuy, { MediaItem } from '@/components/ReelsToBuy';
import { getMockProductBySlug } from '@/mocks/productBySlug';
import { BRAND_PERSON_NAME } from "@/lib/brandConfig";

function getApiBase(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

interface Product {
  id: string;
  name: string;
  description: string;
  slug: string;
  price_usd: number;
  price_ars?: number;
  recommended_price?: number;
  recommended_currency?: string;
  product_type: string;
  product_category: string;
  features?: string[];
  tags?: string[];
  button_text?: string;
  cta_button_text?: string;
  cta_button_color?: string;
  show_quick_buy?: boolean;
  is_featured?: boolean;
  image_url?: string;
  image_alt?: string;
  thumbnail_url?: string;
  hero_image_url?: string;
  gallery_images?: string[];
  video_url?: string;
  page_title?: string;
  page_description?: string;
}

export default function GiftProduct() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    const detectCountry = async (): Promise<string> => {
      // VERDAD ÚNICA: país del Worker (canonicalLocation); fallback al
      // endpoint server-side (headers CF). Nunca proveedores geo del cliente.
      const canonical = getCanonicalLocation().country;
      if (canonical) return canonical;
      try {
        const res = await fetch("/api/detect-country");
        const data = await res.json();
        return data.country && data.country !== "XX" ? data.country : "US";
      } catch {
        return "US";
      }
    };

    const loadMockProduct = (country: string): boolean => {
      const mock = getMockProductBySlug(slug, country);
      if (!mock) return false;
      const p = mock.product as Product;
      if (p?.product_category?.toLowerCase() === 'individual') {
        navigate(`/p/${slug}`, { replace: true });
        return true;
      }
      setProduct(p);
      setError(null);
      setLoading(false);
      return true;
    };

    const loadProduct = async () => {
      try {
        const country = await detectCountry();
        const useMockFromQuery =
          typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('mock') === '1';
        const allowMock = import.meta.env.DEV || useMockFromQuery;
        const isKnownMockSlug =
          slug === 'pack-fotos-premium' ||
          slug === 'video-personalizado' ||
          slug === 'video-personalizado-usd';

        // Mock real para testing en /gift/:slug cuando estamos en desarrollo.
        if (allowMock && isKnownMockSlug) {
          if (loadMockProduct(country)) return;
        }

        // Intentar API real (usa misma base que ProductPageSimple)
        const base = getApiBase();
        const res = await fetch(`${base}/api/products/by-slug?slug=${slug}&country=${country}`);
        if (!res.ok) {
          // Si falla API y existe mock local para ese slug, usarlo
          if (allowMock && loadMockProduct(country)) return;
          throw new Error('Producto no encontrado');
        }

        const data = await res.json();
        const p = data.product;
        // Layout según doc: individual → /p/, otros → /gift/
        if (p?.product_category?.toLowerCase() === 'individual') {
          navigate(`/p/${slug}`, { replace: true });
          return;
        }
        setProduct(p);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error cargando producto');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Producto no encontrado</CardTitle>
            <CardDescription>
              El producto que buscas no existe o ya no está disponible.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')}>
              Volver al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const detectCountry = async (): Promise<string> => {
    // VERDAD ÚNICA: país del Worker (canonicalLocation); fallback al
      // endpoint server-side (headers CF). Nunca proveedores geo del cliente.
      const canonical = getCanonicalLocation().country;
      if (canonical) return canonical;
      try {
        const res = await fetch("/api/detect-country");
        const data = await res.json();
        return data.country && data.country !== "XX" ? data.country : "US";
      } catch {
        return "US";
      }
  };

  const getPaymentProvider = (currency?: string, country?: string): 'stripe' | 'mercadopago' =>
    (currency || '').toUpperCase() === 'ARS' ? 'mercadopago' : 'stripe';

  const handlePurchaseReel = async () => {
    const country = await detectCountry();
    const provider = getPaymentProvider(product.recommended_currency, country);
    const base = getApiBase();
    try {
      if (provider === 'mercadopago') {
        const res = await fetch(`${base}/api/mercadopago/create-preference`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: product.id,
            slug: product.slug,
            productName: product.name,
            price: product.recommended_price ?? product.price_ars ?? product.price_usd,
            currency: product.recommended_currency || (country === 'AR' ? 'ARS' : 'USD'),
            country,
            successPath: `/gift/${product.slug}`,
            failurePath: `/gift/${product.slug}`,
            pendingPath: `/gift/${product.slug}`,
          }),
        });
        if (!res.ok) throw new Error('Error al crear preferencia de MercadoPago');
        const { init_point } = await res.json();
        window.location.href = init_point;
      } else {
        // USD: create-payment-intent → /pagar/{paymentIntentId} (Stripe Elements)
        const res = await fetch(`${base}/api/products/create-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug: product.slug }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.error || 'Error al iniciar el pago');
        }
        const { paymentIntentId } = await res.json();
        if (paymentIntentId) {
          window.location.href = `${base}/pagar/${paymentIntentId}`;
          return;
        }
        navigate(`/buy/${slug}`);
      }
    } catch (err) {
      console.error(err);
      alert('Error al procesar la compra. Intenta de nuevo.');
    }
  };

  const handleQuickBuy = () => navigate(`/buy/${slug}`);

  // SEO: page_title, page_description
  useEffect(() => {
    const t = product?.page_title || product?.name;
    if (t) document.title = `${t} | ${BRAND_PERSON_NAME}`;
    const desc = product?.page_description || product?.description;
    if (desc) {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', String(desc));
    }
  }, [product?.page_title, product?.name, product?.page_description, product?.description]);

  const hasReelContent =
    !!product.video_url || !!product.image_url || (product.gallery_images && product.gallery_images.length > 0);

  const reelItems: MediaItem[] = [];
  if (product.video_url) {
    reelItems.push({
      type: 'video',
      src: product.video_url,
      thumbnail: product.thumbnail_url || product.image_url,
    });
  }
  if (product.image_url) {
    reelItems.push({ type: 'image', src: product.image_url, alt: product.image_alt || product.name });
  }
  if (product.gallery_images?.length) {
    product.gallery_images.forEach((url: string) =>
      reelItems.push({ type: 'image', src: url, alt: product.image_alt || product.name })
    );
  }
  if (hasReelContent && reelItems.length === 0) {
    reelItems.push({
      type: 'image',
      src: 'https://via.placeholder.com/400x600?text=' + encodeURIComponent(product.name),
      alt: product.name,
    });
  }

  if (hasReelContent) {
    const displayPrice = product.recommended_price ?? product.price_usd;
    const displayCurrency = product.recommended_currency || 'USD';
    return (
      <ReelsToBuy
        items={reelItems}
        maxFreeItems={3}
        price={`${displayCurrency} ${displayPrice}`}
        packName={product.name}
        buttonText={product.cta_button_text || product.button_text || 'Comprar Ahora'}
        buttonColor={product.cta_button_color || '#ec4899'}
        heroImage={product.hero_image_url}
        description={product.page_description || product.description}
        analyticsSurface="gift_product"
        productId={product.id}
        productSlug={product.slug}
        priceAmount={displayPrice}
        priceCurrency={displayCurrency}
        onPurchaseClick={handlePurchaseReel}
      />
    );
  }

  const displayPrice = product.recommended_price ?? product.price_usd;
  const displayCurrency = product.recommended_currency || 'USD';
  const paymentProvider = displayCurrency === 'ARS' ? 'MercadoPago' : 'Stripe';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Card className="overflow-hidden">
          {product.is_featured && (
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-center py-2 font-semibold">
              ⭐ Producto Destacado
            </div>
          )}
          
          <CardHeader className="text-center">
            {(product.hero_image_url || product.image_url || product.thumbnail_url) && (
              <div className="mb-6">
                <img
                  src={product.hero_image_url || product.image_url || product.thumbnail_url}
                  alt={product.image_alt || product.name}
                  className="w-full max-h-72 object-cover rounded-xl border"
                />
              </div>
            )}

            <div className="flex justify-center gap-2 mb-4">
              {product.tags?.map((tag, idx) => (
                <Badge key={idx} variant="secondary">
                  {tag}
                </Badge>
              ))}
              {product.video_url && (
                <Badge variant="secondary">Video</Badge>
              )}
            </div>
            
            <CardTitle className="text-4xl font-bold mb-4">
              {product.name}
            </CardTitle>
            
            <CardDescription className="text-lg">
              {product.page_description || product.description}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-8">
            {/* Precio */}
            <div className="text-center">
              <div className="text-5xl font-bold text-purple-600 mb-2">
                {displayCurrency} {displayPrice}
                {product.product_type === 'recurring' && (
                  <span className="text-2xl text-gray-500"> /mes</span>
                )}
              </div>
              
              {product.price_ars && displayCurrency !== 'ARS' && (
                <div className="text-gray-500">
                  o ${product.price_ars} ARS
                </div>
              )}
            </div>

            {/* Características */}
            {product.features && product.features.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold mb-4">
                  ¿Qué incluye?
                </h3>
                <ul className="space-y-3">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-lg">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Botones de compra */}
            <div className="pt-6 space-y-3">
              <Button
                onClick={handlePurchaseReel}
                size="lg"
                className="w-full text-lg py-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                {product.cta_button_text || product.button_text || 'Comprar Ahora'}
              </Button>
              {product.show_quick_buy !== false && (
                <Button
                  onClick={handleQuickBuy}
                  size="lg"
                  variant="outline"
                  className="w-full text-lg py-6"
                >
                  Compra Rápida (1 Click)
                </Button>
              )}
            </div>

            {/* Información adicional */}
            <div className="text-center text-sm text-gray-500 pt-4 border-t">
              <p>
                {product.product_type === 'recurring' 
                  ? 'Suscripción con renovación automática. Cancela cuando quieras.'
                  : 'Pago único. Acceso inmediato después de la compra.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Información de seguridad */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>🔒 Pago seguro procesado por {paymentProvider}</p>
          <p className="mt-2">✨ Garantía de satisfacción</p>
        </div>
      </div>
    </div>
  );
}
